import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AccountCircle,
  Group,
  Delete,
  Edit,
  Save,
  Cancel,
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  OpenInNew
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatNumber, formatDate, formatDateTime } from '../utils/formatters';

// Перевод типов транзакций на русский
const getTransactionTypeLabel = (type) => {
  const types = {
    'deposit': 'Депозит',
    'withdrawal': 'Вывод',
    'star_withdrawal': 'Вывод из звёздного',
    'lunar_withdrawal': 'Вывод из лунного',
    'piggybank_withdrawal': 'Вывод из копилки',
    'piggybank': 'Копилка',
    'piggybank_deposit': 'Клейм в копилку',
    'star': 'Звёздный стейкинг',
    'star_staking': 'Звёздный стейкинг',
    'lunar': 'Лунный стейкинг',
    'lunar_staking': 'Лунный стейкинг',
    'staking': 'Стейкинг',
    'staking_reward': 'Награда за стейкинг',
    'claim': 'Клейм',
    'referral': 'Реферальный бонус',
    'referral_bonus': 'Реферальный бонус',
    'game_win': 'Выигрыш в игре',
    'game_loss': 'Проигрыш в игре',
    'bonus': 'Бонус',
    'admin_add': 'Начисление админом',
    'admin_subtract': 'Списание админом',
    'admin_deposit': 'Начисление админом',
    'marathon': 'Марафон',
    'marathon_deposit': 'Депозит в марафон',
    'marathon_withdrawal': 'Вывод из марафона',
    'task_reward': 'Награда за задание',
    'adsgram_reward': 'Просмотр рекламы',
    'referral_staking': 'Реферал (от стейкинга)',
    'referral_daily': 'Реферал (ежедневный)',
    'referral_daily_usdt': 'Реферал (ежедневный, USDT)',
    'referral_marathon_bonus': 'Реферал (марафон)',
    'referral_marathon_contribution_bonus': 'Реферал (взнос в марафон)',
    'referral_bonus_retroactive': 'Реферал (задним числом)',
    'referral_staking_reward': 'Реферал (награда за стейк)',
    'slot_referral_reward': 'Реферал (слоты)',
    'sugar_referral_gift': 'Реферал (Sugar Strike)',
    'sperm_deposit': 'Депозит SPERM',
    'withdrawal_usdt': 'Вывод USDT',
    'piggybank_usdt_withdraw': 'Вывод USDT из копилки',
    'piggybank_interest': 'Процент по копилке',
    'piggybank_transfer_to_slots': 'Перевод копилки в слоты',
    'slots': 'Слоты',
    'minigame': 'Мини-игра',
    'minigame_payment': 'Оплата мини-игры',
    'game_play': 'Игра',
    'poker_buyin': 'Покер (бай-ин)',
    'poker_buy_in': 'Покер (бай-ин)',
    'poker_cashout': 'Покер (вывод со стола)',
    'taddy_reward': 'Просмотр рекламы (Taddy)',
    'loyalty_chest': 'Сундук лояльности',
    'casino_promo': 'Промокод казино',
    'marathon_promo': 'Промо марафона',
    'marathon_contribution': 'Взнос в марафон',
    'marathon_deposit_usdt': 'Взнос в марафон (USDT)',
    'marathon_profit_claim': 'Клейм прибыли марафона',
    'marathon_profit_withdraw': 'Вывод прибыли марафона',
    'marathon_burn': 'Сгорание в марафоне',
    'marathon_burn_correction': 'Корректировка сгорания',
    'marathon_usdt_balance': 'Марафон → баланс USDT',
    'marathon_usdt_piggybank': 'Марафон → копилка USDT',
    'marathon_overpay_clawback': 'Возврат переплаты марафона',
    'marathon_overpay_clawback_reversal': 'Отмена возврата переплаты',
    'marathon_compensation': 'Компенсация по марафону',
    'lunar_staking': 'Лунный стейкинг',
    'lunar_staking_ton': 'Лунный стейкинг (за TON)',
    'lunar_staking_ton_failed': 'Лунный стейкинг (сбой)',
    'star_staking_ton': 'Звёздный стейкинг (за TON)',
    'staking_deposit': 'Депозит в стейкинг',
    'staking_correction': 'Корректировка стейкинга',
    'admin_usdt_deposit': 'Начисление USDT админом',
    'admin_usdt_withdrawal': 'Списание USDT админом',
    'admin_correction': 'Корректировка админом',
    'admin_adjustment': 'Правка админом',
    'admin_compensation': 'Компенсация от админа',
    'correction': 'Корректировка',
    'correction_duplicate_deposit_bonus': 'Корректировка дубля депозита',
    'correction_duplicate_marathon_bonus': 'Корректировка дубля марафона',
    'deposit_ton_refund': 'Возврат TON-депозита',
    'refund': 'Возврат',
    'burn': 'Сжигание',
    'exchange': 'Обмен',
    'piggybank_interest_usdt': 'Процент по копилке (USDT)'
  };
  return types[type] || type;
};

// Перевод статусов транзакций
const getTransactionStatusLabel = (status) => {
  const statuses = {
    'completed': 'Завершено',
    'pending': 'В ожидании',
    'failed': 'Ошибка',
    'cancelled': 'Отменено',
    'processing': 'Обработка'
  };
  return statuses[status] || status;
};

// Лестница рангов как в игре (settings, category='ranks' → rank_1..17_name).
// НЕ выдумывать: список в админке должен совпадать с бэкендом, иначе
// у игрока с «Паладином» поле пустое и сохранение затирает ранг.
const RANKS = [
  'Новичок', 'Ученик', 'Боец', 'Воин', 'Рыцарь', 'Паладин', 'Витязь',
  'Капитан', 'Мастер', 'Эксперт', 'Чемпион', 'Герой', 'Титан',
  'Легенда', 'Мифик', 'Божество', 'Верховный Консул'
];

// Перевод рангов
const getRankLabel = (rank) => {
  const ranks = {
    'newbie': 'Новичок',
    'bronze': 'Бронза',
    'silver': 'Серебро',
    'gold': 'Золото',
    'platinum': 'Платина'
  };
  return ranks[rank] || rank || 'Новичок';
};

// VIP хранится числом (users.vip_level), строка vip_status считается из него
// на бэкенде. Сохранять нужно ИМЕННО уровень: vip_status в allowedFields нет.
const VIP_LEVELS = [
  { level: 0, label: 'Без VIP' },
  { level: 1, label: 'Bronze VIP' },
  { level: 2, label: 'Silver VIP' },
  { level: 3, label: 'Gold VIP' },
  { level: 4, label: 'Platinum VIP' },
  { level: 5, label: 'Diamond VIP' },
  { level: 6, label: 'Master VIP' },
  { level: 7, label: 'Grandmaster VIP' },
  { level: 8, label: 'Elite VIP' },
  { level: 9, label: 'Legend VIP' },
  { level: 10, label: 'Mythic VIP' }
];

// Перевод VIP статусов
const getVipLabel = (vipStatus) => {
  const vips = {
    'bronze_vip': 'Bronze VIP',
    'silver_vip': 'Silver VIP',
    'gold_vip': 'Gold VIP',
    'platinum_vip': 'Platinum VIP',
    'diamond_vip': 'Diamond VIP',
    '': 'Нет',
    null: 'Нет'
  };
  return vips[vipStatus] || vipStatus || 'Нет';
};

// Перевод названий стран на русский
const getCountryNameRu = (countryCode) => {
  const countries = {
    'AF': 'Афганистан', 'AL': 'Албания', 'DZ': 'Алжир', 'AD': 'Андорра', 'AO': 'Ангола',
    'AR': 'Аргентина', 'AM': 'Армения', 'AU': 'Австралия', 'AT': 'Австрия', 'AZ': 'Азербайджан',
    'BS': 'Багамы', 'BH': 'Бахрейн', 'BD': 'Бангладеш', 'BY': 'Беларусь', 'BE': 'Бельгия',
    'BZ': 'Белиз', 'BJ': 'Бенин', 'BT': 'Бутан', 'BO': 'Боливия', 'BA': 'Босния и Герцеговина',
    'BW': 'Ботсвана', 'BR': 'Бразилия', 'BN': 'Бруней', 'BG': 'Болгария', 'BF': 'Буркина-Фасо',
    'BI': 'Бурунди', 'KH': 'Камбоджа', 'CM': 'Камерун', 'CA': 'Канада', 'CV': 'Кабо-Верде',
    'CF': 'ЦАР', 'TD': 'Чад', 'CL': 'Чили', 'CN': 'Китай', 'CO': 'Колумбия',
    'KM': 'Коморы', 'CG': 'Конго', 'CR': 'Коста-Рика', 'HR': 'Хорватия', 'CU': 'Куба',
    'CY': 'Кипр', 'CZ': 'Чехия', 'DK': 'Дания', 'DJ': 'Джибути', 'DO': 'Доминикана',
    'EC': 'Эквадор', 'EG': 'Египет', 'SV': 'Сальвадор', 'GQ': 'Экваториальная Гвинея', 'ER': 'Эритрея',
    'EE': 'Эстония', 'ET': 'Эфиопия', 'FJ': 'Фиджи', 'FI': 'Финляндия', 'FR': 'Франция',
    'GA': 'Габон', 'GM': 'Гамбия', 'GE': 'Грузия', 'DE': 'Германия', 'GH': 'Гана',
    'GR': 'Греция', 'GT': 'Гватемала', 'GN': 'Гвинея', 'GW': 'Гвинея-Бисау', 'GY': 'Гайана',
    'HT': 'Гаити', 'HN': 'Гондурас', 'HU': 'Венгрия', 'IS': 'Исландия', 'IN': 'Индия',
    'ID': 'Индонезия', 'IR': 'Иран', 'IQ': 'Ирак', 'IE': 'Ирландия', 'IL': 'Израиль',
    'IT': 'Италия', 'JM': 'Ямайка', 'JP': 'Япония', 'JO': 'Иордания', 'KZ': 'Казахстан',
    'KE': 'Кения', 'KP': 'КНДР', 'KR': 'Южная Корея', 'KW': 'Кувейт', 'KG': 'Кыргызстан',
    'LA': 'Лаос', 'LV': 'Латвия', 'LB': 'Ливан', 'LS': 'Лесото', 'LR': 'Либерия',
    'LY': 'Ливия', 'LI': 'Лихтенштейн', 'LT': 'Литва', 'LU': 'Люксембург', 'MK': 'Северная Македония',
    'MG': 'Мадагаскар', 'MW': 'Малави', 'MY': 'Малайзия', 'MV': 'Мальдивы', 'ML': 'Мали',
    'MT': 'Мальта', 'MR': 'Мавритания', 'MU': 'Маврикий', 'MX': 'Мексика', 'MD': 'Молдова',
    'MC': 'Монако', 'MN': 'Монголия', 'ME': 'Черногория', 'MA': 'Марокко', 'MZ': 'Мозамбик',
    'MM': 'Мьянма', 'NA': 'Намибия', 'NP': 'Непал', 'NL': 'Нидерланды', 'NZ': 'Новая Зеландия',
    'NI': 'Никарагуа', 'NE': 'Нигер', 'NG': 'Нигерия', 'NO': 'Норвегия', 'OM': 'Оман',
    'PK': 'Пакистан', 'PA': 'Панама', 'PG': 'Папуа-Новая Гвинея', 'PY': 'Парагвай', 'PE': 'Перу',
    'PH': 'Филиппины', 'PL': 'Польша', 'PT': 'Португалия', 'QA': 'Катар', 'RO': 'Румыния',
    'RU': 'Россия', 'RW': 'Руанда', 'SA': 'Саудовская Аравия', 'SN': 'Сенегал', 'RS': 'Сербия',
    'SG': 'Сингапур', 'SK': 'Словакия', 'SI': 'Словения', 'SO': 'Сомали', 'ZA': 'ЮАР',
    'ES': 'Испания', 'LK': 'Шри-Ланка', 'SD': 'Судан', 'SR': 'Суринам', 'SE': 'Швеция',
    'CH': 'Швейцария', 'SY': 'Сирия', 'TW': 'Тайвань', 'TJ': 'Таджикистан', 'TZ': 'Танзания',
    'TH': 'Таиланд', 'TG': 'Того', 'TN': 'Тунис', 'TR': 'Турция', 'TM': 'Туркменистан',
    'UG': 'Уганда', 'UA': 'Украина', 'AE': 'ОАЭ', 'GB': 'Великобритания', 'US': 'США',
    'UY': 'Уругвай', 'UZ': 'Узбекистан', 'VE': 'Венесуэла', 'VN': 'Вьетнам', 'YE': 'Йемен',
    'ZM': 'Замбия', 'ZW': 'Зимбабве', 'HK': 'Гонконг', 'SL': 'Сьерра-Леоне', 'SC': 'Сейшелы'
  };
  if (!countryCode) return 'Не определена';
  return countries[countryCode.toUpperCase()] || countryCode;
};

// Статусы движения денег (выводы живут в своей таблице со своими статусами)
const getMoneyStatusLabel = (status) => ({
  approved: 'Отправлен',
  completed: 'Завершён',
  pending: 'В ожидании',
  processing: 'Обработка',
  rejected: 'Отклонён',
  cancelled: 'Отменён',
  failed: 'Ошибка'
}[status] || status);

const MONEY_STATUS_COLOR = {
  approved: '#22c55e',
  completed: '#22c55e',
  pending: '#f59e0b',
  processing: '#f59e0b',
  rejected: '#ef4444',
  cancelled: '#94a3b8',
  failed: '#ef4444'
};

// Группы типов для фильтра вкладки «Транзакции» (без фильтра лента тонет в спинах)
const TX_GROUPS = {
  money: {
    label: '💵 Деньги',
    types: ['deposit', 'sperm_deposit', 'withdrawal', 'withdrawal_usdt', 'star_withdrawal',
      'piggybank_withdrawal', 'piggybank_usdt_withdraw', 'marathon_profit_withdraw',
      'admin_deposit', 'admin_usdt_deposit', 'admin_withdrawal', 'admin_usdt_withdrawal',
      'marathon_deposit_usdt', 'marathon_contribution', 'refund', 'correction']
  },
  staking: {
    label: '📈 Стейк и копилка',
    types: ['lunar_staking', 'star_staking', 'lunar_staking_ton', 'star_staking_ton',
      'staking_deposit', 'staking_reward', 'claim', 'piggybank_deposit',
      'piggybank_interest', 'piggybank_transfer_to_slots', 'marathon_profit_claim',
      'marathon_burn']
  },
  game: {
    label: '🎰 Игра',
    types: ['slots', 'minigame', 'minigame_payment', 'game_play', 'poker_buyin', 'poker_cashout']
  },
  referral: {
    label: '👥 Рефералка',
    types: ['referral_daily', 'referral_staking', 'referral_bonus', 'referral_marathon_bonus',
      'referral_daily_usdt', 'slot_referral_reward', 'sugar_referral_gift',
      'referral_marathon_contribution_bonus']
  },
  bonus: {
    label: '🎁 Бонусы',
    types: ['task_reward', 'adsgram_reward', 'taddy_reward', 'loyalty_chest',
      'casino_promo', 'marathon_promo', 'admin_compensation']
  }
};

const TX_PERIODS = [
  { key: 'all', label: 'Всё время', days: null },
  { key: '90', label: '90 дней', days: 90 },
  { key: '30', label: '30 дней', days: 30 },
  { key: '7', label: '7 дней', days: 7 }
];

// Одинаковое оформление полей в режиме редактирования
const EDIT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: '#334155' }
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiFormHelperText-root': { color: '#64748b' },
  '& .Mui-disabled': { WebkitTextFillColor: '#64748b' }
};

const shortHash = (h) => (!h ? '' : h.length > 18 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);

const copyToClipboard = (text) => {
  if (!text) return;
  try {
    navigator.clipboard.writeText(text);
  } catch (e) {
    // старый браузер — просто молча пропускаем
  }
};

// Мини-карточка метрики: одинаковый размер у всех, поэтому верх не «съезжает»
const StatCard = ({ label, value, sub, color = '#e2e8f0', accent = '#334155' }) => (
  <Paper sx={{
    p: 1.5,
    height: '100%',
    bgcolor: '#0f172a',
    border: `1px solid ${accent}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0
  }}>
    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography sx={{
      color,
      fontWeight: 700,
      fontSize: { xs: '1rem', sm: '1.15rem' },
      lineHeight: 1.3,
      wordBreak: 'break-word'
    }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.3 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// Метрики верхней панели одним списком: на телефоне это лента, на десктопе сетка
const getMetrics = (user, bt) => {
  const net = bt?.net_ton || 0;
  return [
    {
      label: 'Баланс SPERM',
      value: formatNumber(user.balance, 0),
      sub: parseFloat(user.frozen_balance || 0) > 0 ? `заморожено ${formatNumber(user.frozen_balance, 0)}` : null,
      color: '#10b981',
      accent: '#10b981'
    },
    {
      // §06.09.2026 (владелец развёл кошельки): марафонный USDT — тот, которым
      // играть в казино НЕЛЬЗЯ. Раньше баланс был один на всё.
      label: 'USDT марафон',
      value: formatNumber(user.usdt_balance || 0, 2),
      color: '#22c55e',
      accent: '#22c55e'
    },
    {
      label: 'USDT казино',
      value: formatNumber(user.casino_usdt_balance || 0, 2),
      color: '#f472b6',
      accent: '#f472b6'
    },
    {
      label: 'В стейке (заблок.)',
      value: formatNumber(user.earnings_breakdown?.staked_locked || 0, 0),
      sub: 'вывести нельзя',
      color: '#f59e0b',
      accent: '#f59e0b'
    },
    {
      label: 'Завёл ончейн',
      value: `${formatNumber(bt?.sperm_deposited_ton || 0, 2)} TON`,
      sub: `${formatNumber(bt?.sperm_deposited || 0, 0)} SPERM`,
      color: '#38bdf8',
      accent: '#38bdf8'
    },
    {
      label: 'Вывел ончейн',
      value: `${formatNumber(bt?.sperm_withdrawn_ton || 0, 2)} TON`,
      sub: `${formatNumber(bt?.sperm_withdrawn || 0, 0)} SPERM`,
      color: '#a78bfa',
      accent: '#a78bfa'
    },
    {
      label: net >= 0 ? 'Мы в плюсе' : 'Мы в минусе',
      value: `${net >= 0 ? '+' : ''}${formatNumber(net, 2)} TON`,
      sub: bt?.net_usd != null
        ? `${bt.net_usd >= 0 ? '+' : ''}$${formatNumber(bt.net_usd, 2)}`
        : 'завёл − вывел',
      color: net >= 0 ? '#22c55e' : '#ef4444',
      accent: net >= 0 ? '#22c55e' : '#ef4444'
    }
  ];
};

const UserDetailsModal = ({ open, onClose, userId, onUserUpdated }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [stakings, setStakings] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [referralChain, setReferralChain] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [blockchainTurnover, setBlockchainTurnover] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [balanceChange, setBalanceChange] = useState('');
  const [usdtBalanceChange, setUsdtBalanceChange] = useState('');
  // §07.09.2026: казино-USDT правится отдельным полем — кошельки разведены 06.09
  const [casinoUsdtChange, setCasinoUsdtChange] = useState('');
  const [moneyFlow, setMoneyFlow] = useState([]);
  const [moneyTotals, setMoneyTotals] = useState(null);
  const [moneyLoading, setMoneyLoading] = useState(false);
  const [moneyLoaded, setMoneyLoaded] = useState(false);
  const [moneyShown, setMoneyShown] = useState(50);
  const [txPeriod, setTxPeriod] = useState('all');
  const [txGroup, setTxGroup] = useState('all');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // На телефоне паспорт свёрнут: иначе до вкладок надо листать целый экран
  const [profileOpen, setProfileOpen] = useState(false);

  const loadUserDetails = useCallback(async () => {

    setLoading(true);
    setError('');
    
    try {
      // Загружаем данные пользователя

      const userResponse = await adminAPI.getUserById(userId);

      if (userResponse.data.success && userResponse.data.data) {
        setUser(userResponse.data.data);
        
        // Загружаем реферальную цепочку
        try {
          const chainResponse = await adminAPI.getUserReferralChain(userId);
          if (chainResponse.data.success) {
            setReferralChain(chainResponse.data.data || []);
          }
        } catch (chainError) {

          setReferralChain([]);
        }
        
        // Загружаем предупреждения пользователя
        try {
          const warningsResponse = await adminAPI.getUserWarnings(userId);
          if (warningsResponse.data.success) {
            setWarnings(warningsResponse.data.data || []);
          }
        } catch (warningsError) {
          setWarnings([]);
        }

        // Загружаем блокчейн-обороты (сколько реально завёл/вывел ончейн)
        try {
          const turnoverResponse = await adminAPI.getUserBlockchainTurnover(userId);
          if (turnoverResponse.data.success) {
            setBlockchainTurnover(turnoverResponse.data.data);
          }
        } catch (turnoverError) {
          setBlockchainTurnover(null);
        }
      } else {
        setError('Пользователь не найден в базе данных');
      }
      
    } catch (error) {

      if (error.response?.status === 404) {
        setError('Пользователь не найден в базе данных');
      } else {
        setError('Ошибка загрузки данных пользователя');
      }

    } finally {

      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) {
      setMoneyFlow([]);
      setMoneyTotals(null);
      setMoneyLoaded(false);
      setTransactions([]);
      setTransactionsTotal(0);
      loadUserDetails();
    }
  }, [open, userId, loadUserDetails]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Загружаем стейкинги при переходе на вкладку
    if (newValue === 3 && user) {
      loadStakings();
    }
    // Загружаем рефералов при переходе на вкладку
    if (newValue === 4 && user) {
      loadReferrals();
    }
  };

  const loadReferrals = async () => {
    try {
      // Загружаем рефералов
      const response = await adminAPI.getUserReferralsDetailed(userId);
      if (response.data.success) {
        setReferrals(response.data.data?.referrals || []);
      }

      // Загружаем статистику по линиям
      const statsResponse = await adminAPI.getUserReferralStats(userId);
      if (statsResponse.data.success) {
        setReferralStats(statsResponse.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки рефералов:', error);
      setReferrals([]);
    }
  };

  // Загрузка транзакций: период и группа типов задаются админом, по умолчанию — ВСЁ время.
  // Раньше здесь было жёстко «минус месяц», из-за чего старые депозиты просто исчезали.
  const loadTransactions = useCallback(async (page = 1, period = txPeriod, group = txGroup) => {
    try {
      const params = { userId, limit: 50, page };

      const days = TX_PERIODS.find(p => p.key === period)?.days;
      if (days) {
        const from = new Date();
        from.setDate(from.getDate() - days);
        params.startDate = from.toISOString().split('T')[0];
      }
      if (group !== 'all' && TX_GROUPS[group]) {
        params.type = TX_GROUPS[group].types.join(',');
      }

      const transResponse = await adminAPI.getTransactions(params);
      if (transResponse.data.success) {
        setTransactions(Array.isArray(transResponse.data.data) ? transResponse.data.data : []);
        setTransactionsTotal(transResponse.data.pagination?.total || transResponse.data.data?.length || 0);
        setTransactionsPage(page);
      }
    } catch (error) {
      console.error('Ошибка загрузки транзакций:', error);
    }
  }, [userId, txPeriod, txGroup]);

  // Движение живых денег: вводы и выводы за всё время, с ончейн-хэшами
  const loadMoneyFlow = useCallback(async () => {
    setMoneyLoading(true);
    try {
      const response = await adminAPI.getUserMoneyFlow(userId);
      if (response.data.success) {
        setMoneyFlow(response.data.data?.rows || []);
        setMoneyTotals(response.data.data?.totals || null);
        setMoneyShown(50);
      }
    } catch (error) {
      console.error('Ошибка загрузки движения денег:', error);
      setMoneyFlow([]);
      setMoneyTotals(null);
    } finally {
      // ставим флаг в любом случае: иначе при ошибке API эффект дёргал бы запрос заново
      setMoneyLoaded(true);
      setMoneyLoading(false);
    }
  }, [userId]);

  // Вкладки «Ввод/Вывод» и «Транзакции» грузятся лениво — карточка открывается быстрее
  useEffect(() => {
    if (open && user && activeTab === 1 && !moneyLoaded && !moneyLoading) {
      loadMoneyFlow();
    }
  }, [open, user, activeTab, moneyLoaded, moneyLoading, loadMoneyFlow]);

  useEffect(() => {
    if (open && user && activeTab === 2) {
      loadTransactions(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, activeTab, txPeriod, txGroup]);

  const loadStakings = async () => {

    try {
      const response = await adminAPI.getUserStakings(userId);

      if (response.data.success) {
        const stakingsData = response.data.data || [];

        setStakings(stakingsData);
      }
    } catch (error) {

      setStakings([]);
    }
  };

  const handleDeleteStaking = async (stakingId) => {

    // Используем встроенное подтверждение Material-UI вместо window.confirm
    const confirmDelete = window.confirm('Вы уверены, что хотите удалить этот стейкинг? Средства будут возвращены на баланс.');
    
    if (!confirmDelete) {

      return;
    }

    try {
      const response = await adminAPI.deleteStaking(stakingId);

      if (response.data && response.data.success) {

        // Обновляем список стейкингов
        const prevStakings = [...stakings];
        const newStakings = prevStakings.filter(s => s.id !== stakingId);

        setStakings(newStakings);
        
        // Перезагружаем данные пользователя для обновления баланса

        await loadUserDetails();
        
        // Перезагружаем стейкинги

        await loadStakings();
        
        // Показываем уведомление об успешном удалении
        alert('Стейкинг успешно удален! Средства возвращены на баланс пользователя.');

      } else {

        alert('Ошибка при удалении стейкинга: ' + (response.data?.error || 'Неизвестная ошибка'));
      }
    } catch (error) {

      alert('Ошибка при удалении стейкинга. Проверьте консоль для деталей.');
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Отмена редактирования
      setEditMode(false);
      setEditedUser(null);
    } else {
      // Начало редактирования
      setEditMode(true);
      setEditedUser({ ...user });
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      let balanceUpdated = false;
      let usdtUpdated = false;

      // Обрабатываем изменение SPERM баланса, если оно есть
      if (balanceChange && parseFloat(balanceChange) !== parseFloat(user.balance)) {
        const newBalance = parseFloat(balanceChange);
        const oldBalance = parseFloat(user.balance);
        const amount = newBalance - oldBalance;

        const balanceResponse = await adminAPI.updateUserBalance(userId, {
          amount: amount,
          type: amount > 0 ? 'add' : 'subtract',
          reason: 'Изменение через админ-панель'
        });

        if (!balanceResponse.data.success) {
          alert('Ошибка при изменении баланса: ' + (balanceResponse.data.error || 'Неизвестная ошибка'));
          setSaving(false);
          return;
        }

        balanceUpdated = true;
      }

      // Обрабатываем изменение USDT баланса, если оно есть
      if (usdtBalanceChange && parseFloat(usdtBalanceChange) !== parseFloat(user.usdt_balance || 0)) {
        const newUsdtBalance = parseFloat(usdtBalanceChange);
        const oldUsdtBalance = parseFloat(user.usdt_balance || 0);
        const amount = newUsdtBalance - oldUsdtBalance;

        const usdtResponse = await adminAPI.updateUserUsdtBalance(userId, {
          amount: Math.abs(amount),
          type: amount > 0 ? 'add' : 'subtract',
          reason: 'Изменение USDT через админ-панель'
        });

        if (!usdtResponse.data.success) {
          alert('Ошибка при изменении USDT баланса: ' + (usdtResponse.data.error || 'Неизвестная ошибка'));
          setSaving(false);
          return;
        }

        usdtUpdated = true;
      }

      // Казино-USDT (Sugar Strike) — отдельный кошелёк, отдельный вызов
      if (casinoUsdtChange && parseFloat(casinoUsdtChange) !== parseFloat(user.casino_usdt_balance || 0)) {
        const amount = parseFloat(casinoUsdtChange) - parseFloat(user.casino_usdt_balance || 0);

        const casinoResponse = await adminAPI.updateUserUsdtBalance(userId, {
          amount: Math.abs(amount),
          type: amount > 0 ? 'add' : 'subtract',
          wallet: 'casino',
          reason: 'Изменение казино-USDT через админ-панель'
        });

        if (!casinoResponse.data.success) {
          alert('Ошибка при изменении казино-USDT: ' + (casinoResponse.data.message || casinoResponse.data.error || 'Неизвестная ошибка'));
          setSaving(false);
          return;
        }

        usdtUpdated = true;
      }

      // Затем сохраняем остальные изменения
      const changes = {};
      if (editedUser) {
        Object.keys(editedUser).forEach(key => {
          if (editedUser[key] !== user[key] && key !== 'balance') {
            changes[key] = editedUser[key];
          }
        });
      }

      if (Object.keys(changes).length > 0) {

        const response = await adminAPI.updateUser(userId, changes);

        if (!response.data.success) {
          alert('Ошибка при сохранении: ' + (response.data.error || 'Неизвестная ошибка'));
          setSaving(false);
          return;
        }
      }
      
      // Показываем уведомление только если были изменения
      if (balanceUpdated || usdtUpdated || Object.keys(changes).length > 0) {
        window.alert('Изменения успешно сохранены!');
      } else {
        window.alert('Нет изменений для сохранения');
      }

      // Перезагружаем данные пользователя
      await loadUserDetails();

      // Сбрасываем состояние редактирования
      setEditMode(false);
      setEditedUser(null);
      setBalanceChange('');
      setUsdtBalanceChange('');
      setCasinoUsdtChange('');
      
      // Обновляем список пользователей если передан callback
      if (onUserUpdated) {
        onUserUpdated();
      }
    } catch (error) {

      alert('Ошибка при сохранении данных: ' + error.message);
    } finally {
      setSaving(false);

    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: '#1e293b',
          color: 'white',
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#1e293b', color: 'white', py: 1.5, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {user?.photo_url ? (
              <Avatar src={user.photo_url} sx={{ width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }} />
            ) : (
              <Avatar sx={{ width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, bgcolor: '#334155' }}>
                <AccountCircle sx={{ fontSize: 32, color: '#94a3b8' }} />
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                lineHeight: 1.2,
                wordBreak: 'break-word',
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                {user?.username ? `@${user.username}` : [user?.first_name, user?.last_name].filter(Boolean).join(' ') || `ID ${userId}`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.3 }}>
                ID {user?.id || userId}
                {user?.telegram_id && (parseInt(user.telegram_id) < 0 ? ' · 🌐 web' : ` · TG ${user.telegram_id}`)}
                {!isMobile && user?.created_at && ` · с ${formatDate(user.created_at)}`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {user?.is_blocked && (
                  <Chip size="small" label="🚫 Забанен" sx={{ bgcolor: '#7f1d1d', color: '#fecaca', height: 20 }} />
                )}
                {user?.withdrawal_blacklisted && (
                  <Chip size="small" label="🚫 Выводы" sx={{ bgcolor: '#7c2d12', color: '#fed7aa', height: 20 }} />
                )}
                {user?.referral_suspended && (
                  <Chip size="small" label="⏸️ Рефералка" sx={{ bgcolor: '#78350f', color: '#fde68a', height: 20 }} />
                )}
                {user?.self_excluded_until && new Date(user.self_excluded_until) > new Date() && (
                  <Chip size="small" label="🛑 Самозапрет" sx={{ bgcolor: '#581c87', color: '#e9d5ff', height: 20 }} />
                )}
                {user?.is_vpn_user && (
                  <Chip size="small" label="VPN" sx={{ bgcolor: '#334155', color: '#cbd5e1', height: 20 }} />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {!editMode ? (
              <IconButton
                onClick={handleEditToggle}
                sx={{ color: '#6366f1' }}
                title="Редактировать"
              >
                <Edit />
              </IconButton>
            ) : (
              <>
                <IconButton
                  onClick={handleSaveChanges}
                  disabled={saving}
                  sx={{ color: '#10b981' }}
                  title="Сохранить изменения"
                >
                  <Save />
                </IconButton>
                <IconButton
                  onClick={handleEditToggle}
                  disabled={saving}
                  sx={{ color: '#ef4444' }}
                  title="Отменить редактирование"
                >
                  <Cancel />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{
        bgcolor: '#1e293b',
        color: 'white',
        borderColor: '#334155',
        px: { xs: 1.5, sm: 3 },
        py: 2
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : user ? (
          <>
            {/* Верх карточки: сначала деньги и статус, потом паспорт юзера */}
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Username"
                        value={editedUser?.username || ''}
                        onChange={(e) => handleFieldChange('username', e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="Имя"
                        value={editedUser?.first_name || ''}
                        onChange={(e) => handleFieldChange('first_name', e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="Фамилия"
                        value={editedUser?.last_name || ''}
                        onChange={(e) => handleFieldChange('last_name', e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="📧 Email"
                        value={editedUser?.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="user@email.com"
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="Telegram ID"
                        value={user.telegram_id}
                        disabled
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={EDIT_FIELD_SX}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Баланс SPERM"
                        type="number"
                        value={balanceChange || user.balance}
                        onChange={(e) => setBalanceChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        helperText={`Сейчас: ${formatNumber(user.balance, 2)} SPERM`}
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="Баланс USDT (марафон)"
                        type="number"
                        value={usdtBalanceChange || user.usdt_balance || 0}
                        onChange={(e) => setUsdtBalanceChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        helperText={`Сейчас: ${formatNumber(user.usdt_balance || 0, 2)} USDT`}
                        sx={EDIT_FIELD_SX}
                      />
                      <TextField
                        label="Баланс USDT (казино)"
                        type="number"
                        value={casinoUsdtChange || user.casino_usdt_balance || 0}
                        onChange={(e) => setCasinoUsdtChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        helperText={`Сейчас: ${formatNumber(user.casino_usdt_balance || 0, 2)} USDT · Sugar Strike`}
                        sx={EDIT_FIELD_SX}
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#94a3b8' }}>Ранг</InputLabel>
                        <Select
                          value={editedUser?.rank || 'Новичок'}
                          onChange={(e) => handleFieldChange('rank', e.target.value)}
                          label="Ранг"
                          sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                            '& .MuiSvgIcon-root': { color: '#94a3b8' }
                          }}
                        >
                          {RANKS.map((r) => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#94a3b8' }}>VIP статус</InputLabel>
                        <Select
                          value={Number(editedUser?.vip_level ?? 0)}
                          onChange={(e) => handleFieldChange('vip_level', Number(e.target.value))}
                          label="VIP статус"
                          sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                            '& .MuiSvgIcon-root': { color: '#94a3b8' }
                          }}
                        >
                          {VIP_LEVELS.map((v) => (
                            <MenuItem key={v.level} value={v.level}>{v.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <>
                  {/* Деньги: на телефоне лента с горизонтальным скроллом, на большом экране сетка */}
                  {isMobile ? (
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      pb: 1,
                      WebkitOverflowScrolling: 'touch',
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#334155', borderRadius: 2 }
                    }}>
                      {getMetrics(user, blockchainTurnover).map((m) => (
                        <Box key={m.label} sx={{ minWidth: 138, flexShrink: 0 }}>
                          <StatCard {...m} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Grid container spacing={1.5}>
                      {getMetrics(user, blockchainTurnover).map((m) => (
                        <Grid item xs={6} sm={4} md={2} key={m.label}>
                          <StatCard {...m} />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* На телефоне паспорт прячем под кнопку — иначе до вкладок целый экран листать */}
                  {isMobile && (
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => setProfileOpen(!profileOpen)}
                      sx={{ mt: 1, color: '#94a3b8', borderColor: '#334155', justifyContent: 'space-between' }}
                      variant="outlined"
                    >
                      Профиль, кошелёк, гео
                      <Box component="span" sx={{ color: '#6366f1' }}>{profileOpen ? '▲' : '▼'}</Box>
                    </Button>
                  )}

                  {(!isMobile || profileOpen) && (
                  <>
                  <Divider sx={{ my: 2, borderColor: '#334155' }} />

                  <Grid container spacing={2}>
                    {/* Паспорт */}
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={1}>
                        {[
                          { label: 'Имя', value: [user.first_name, user.last_name].filter(Boolean).join(' ') || '—' },
                          { label: 'Username', value: user.username ? `@${user.username}` : 'не указан', color: user.username ? '#6366f1' : '#64748b' },
                          { label: 'Email', value: user.email || '—', color: user.email ? '#22c55e' : '#64748b' },
                          { label: 'Язык', value: user.language_code || 'ru' },
                          { label: 'Регистрация', value: formatDate(user.created_at) },
                          { label: 'Последняя активность', value: formatDateTime(user.last_active) },
                          { label: 'Ранг', value: getRankLabel(user.rank), color: '#eab308' },
                          { label: 'VIP', value: getVipLabel(user.vip_status), color: user.vip_status ? '#a855f7' : '#64748b' },
                          { label: 'Рефералы', value: user.referral_count || 0, color: '#06b6d4' },
                          { label: 'Claims', value: user.total_claims || 0, color: '#8b5cf6' },
                          { label: 'Страна', value: `${getCountryNameRu(user.country_code)}${user.city ? `, ${user.city}` : ''}` },
                          { label: 'IP', value: user.ip_address || 'не определён' }
                        ].map((row) => (
                          <Grid item xs={6} key={row.label}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.2 }}>
                              {row.label}
                            </Typography>
                            <Typography variant="body2" sx={{ color: row.color || '#e2e8f0', wordBreak: 'break-word' }}>
                              {row.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* Кошелёк и связи */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(56, 189, 248, 0.08)', border: '1px solid #334155', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                          Кошелёк игрока
                        </Typography>
                        {user.wallet_address ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: '#38bdf8', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                              {user.wallet_address}
                            </Typography>
                            <Tooltip title="Скопировать">
                              <IconButton size="small" onClick={() => copyToClipboard(user.wallet_address)} sx={{ color: '#64748b' }}>
                                <ContentCopy sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Открыть в Tonviewer">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`https://tonviewer.com/${user.wallet_address}`, '_blank')}
                                sx={{ color: '#64748b' }}
                              >
                                <OpenInNew sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#64748b' }}>не привязан</Typography>
                        )}
                      </Box>

                      {(user.referrer_username || user.referrer_telegram_id) && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(16, 185, 129, 0.08)', border: '1px solid #334155', borderRadius: 2 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                            Пригласил
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#10b981' }}>
                            {user.referrer_username ? `@${user.referrer_username}` : `ID ${user.referrer_telegram_id}`}
                          </Typography>
                        </Box>
                      )}

                      {referralChain && referralChain.length > 1 && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#0f172a', border: '1px solid #334155', borderRadius: 2 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                            Реферальная цепочка
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, pl: 1, borderLeft: '2px solid #10b981' }}>
                            {referralChain.map((member, index) => (
                              <Typography
                                key={member.id}
                                variant="body2"
                                sx={{ color: index === 0 ? '#fbbf24' : '#10b981', fontWeight: index === 0 ? 600 : 400 }}
                              >
                                {referralChain.length - index}. {member.username ? `@${member.username}` : member.telegram_id}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  </>
                  )}
                </>
              )}
            </Paper>

            {/* Вкладки с дополнительной информацией */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    minWidth: { xs: 60, sm: 90 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                    py: { xs: 0.5, sm: 1 }
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#6366f1'
                  }
                }}
              >
                <Tab label={isMobile ? '📊' : '📊 Обзор'} />
                <Tab label={isMobile ? '💵 Деньги' : '💵 Ввод / Вывод'} />
                <Tab label={isMobile ? '🧾 Транз' : '🧾 Транзакции'} />
                <Tab label={isMobile ? '📈 Стейк' : '📈 Стейкинг'} />
                <Tab label={isMobile ? '👥 Реф' : '👥 Рефералы'} />
                <Tab label={`⚠️${warnings.length > 0 ? ` ${warnings.length}` : ''}${isMobile ? '' : ' Предупреждения'}`} />
                <Tab label={isMobile ? '⚙️' : '⚙️ Настройки'} />
              </Tabs>
            </Box>

            <Box sx={{ mt: 2, minHeight: 300 }}>
              {/* Статистика */}
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  {/* БЛОК: ОТКУДА ДЕНЬГИ */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: '#0f172a', border: '2px solid #6366f1' }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#6366f1', fontWeight: 'bold' }}>
                        💰 Откуда деньги (полная разбивка)
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Заработано на стейкингах */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(16, 185, 129, 0.1)', borderRadius: 2, border: '1px solid #10b981' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              📈 Заработано по стейкингам (пассив)
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                              {formatNumber(user.earnings_breakdown?.staking_earned || 0, 2)} SPERM
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Реферальные доходы */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2, border: '1px solid #6366f1' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              👥 Заработано по рефералам (всего)
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                              {formatNumber(user.earnings_breakdown?.referral_total_sperm || 0, 2)} SPERM
                            </Typography>
                            <Divider sx={{ my: 1, borderColor: '#334155' }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>Единовременные (% от депозитов):</Typography>
                              <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                                {formatNumber(user.earnings_breakdown?.referral_onetime_sperm || 0, 2)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>Ежедневные (% от дохода):</Typography>
                              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                                {formatNumber(user.earnings_breakdown?.referral_daily_sperm || 0, 2)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Слоты */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(251, 191, 36, 0.1)', borderRadius: 2, border: '1px solid #fbbf24' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              🎰 Слоты (оборот по движку, справочно)
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Выиграно (выплачено):</Typography>
                                <Typography variant="body2" sx={{ color: '#10b981' }}>
                                  +{formatNumber(user.earnings_breakdown?.games_won || 0, 2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Поставлено (ставки):</Typography>
                                <Typography variant="body2" sx={{ color: '#ef4444' }}>
                                  {formatNumber(user.earnings_breakdown?.games_lost || 0, 2)}
                                </Typography>
                              </Box>
                              <Divider sx={{ my: 1, borderColor: '#334155' }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 'bold' }}>RTP игрока:</Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 'bold' }}>
                                  {(user.earnings_breakdown?.games_lost || 0) > 0
                                    ? (100 * (user.earnings_breakdown?.games_won || 0) / user.earnings_breakdown.games_lost).toFixed(1)
                                    : '0'}%
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                Спинов: {user.earnings_breakdown?.spins_count || 0}
                              </Typography>
                              <Divider sx={{ my: 1, borderColor: '#334155' }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Итог по слотам (движок):</Typography>
                                <Typography variant="body2" sx={{
                                  color: (user.earnings_breakdown?.slots_net_profit || 0) >= 0 ? '#ef4444' : '#10b981',
                                  fontWeight: 'bold'
                                }}>
                                  {(user.earnings_breakdown?.slots_net_profit || 0) >= 0 ? '+' : ''}{formatNumber(user.earnings_breakdown?.slots_net_profit || 0, 0)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                «+» = игрок выиграл у нас, «−» = мы его обыграли.
                                Лоялка-разблок: +{formatNumber(user.earnings_breakdown?.moonstash_unlocked || 0, 0)}.
                                По движку (может слегка занижать). Реальный итог по деньгам —
                                в блоке «Денежный итог».
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Задания */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2, border: '1px solid #3b82f6' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              📋 Заработано за задания
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                              {formatNumber(user.earnings_breakdown?.task_earned || 0, 2)} SPERM
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Реклама (AdsGram) */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(236, 72, 153, 0.1)', borderRadius: 2, border: '1px solid #ec4899' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              📺 Заработано за рекламу (AdsGram)
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#ec4899', fontWeight: 'bold' }}>
                              {formatNumber(user.earnings_breakdown?.ad_earned || 0, 2)} SPERM
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                              Просмотров: {user.earnings_breakdown?.ad_views || 0}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Лояльность / Вагер */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(168, 85, 247, 0.1)', borderRadius: 2, border: '1px solid #a855f7' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              🎁 Лояльность / Вагер
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>MoonStash разблокировано:</Typography>
                                <Typography variant="body2" sx={{ color: '#10b981' }}>
                                  +{formatNumber(user.earnings_breakdown?.moonstash_unlocked || 0, 2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>MoonStash в отыгрыше (заблок.):</Typography>
                                <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                                  {formatNumber(user.earnings_breakdown?.moonstash_locked || 0, 2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Всего отыграно (вагер):</Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  {formatNumber(user.earnings_breakdown?.total_wagered || 0, 0)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Стейкинг — отдельный итог игрока */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2, border: '1px solid #3b82f6' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              💎 Стейкинг (итог игрока)
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Наэмитил профита (счётчик):</Typography>
                                <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                  +{formatNumber(user.earnings_breakdown?.staking_emitted || 0, 0)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Тело в стейке (заблок.):</Typography>
                                <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                                  {formatNumber(user.earnings_breakdown?.staked_locked || 0, 0)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                «Наэмитил» — счётчик профита, что стейк начислил (расход by
                                design). «Тело» — принципал, его игрок выводит отдельно.
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Объективный денежный итог (реконсилится с реальными деньгами) */}
                        <Grid item xs={12} md={4}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(20, 184, 166, 0.12)', borderRadius: 2, border: '1px solid #14b8a6' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              💰 Денежный итог (объективно)
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Внёс всего (вкл. прямой стейк):</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(user.earnings_breakdown?.total_deposited || 0, 0)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Вывел всего:</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(user.earnings_breakdown?.total_withdrawn || 0, 0)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>На руках (доступно к выводу):</Typography>
                                <Typography variant="body2" sx={{ color: '#10b981' }}>
                                  {formatNumber(user.earnings_breakdown?.free_balance || 0, 0)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>В стейке (заблок., вывести нельзя):</Typography>
                                <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                                  {formatNumber(user.earnings_breakdown?.staked_locked || 0, 0)}
                                </Typography>
                              </Box>
                              <Divider sx={{ my: 1, borderColor: '#334155' }} />
                              {(() => {
                                const eb = user.earnings_breakdown || {};
                                const realized = eb.realized_pnl || 0;
                                const potential = eb.net_potential || 0;
                                const sgn = (n) => (n >= 0 ? '+' : '');
                                const col = (n) => (n >= 0 ? '#ef4444' : '#10b981');
                                return (
                                  <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 'bold' }}>
                                        PNL ({realized >= 0 ? 'игрок в ПЛЮСЕ' : 'мы в ПЛЮСЕ'}):
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: col(realized), fontWeight: 'bold' }}>
                                        {sgn(realized)}{formatNumber(realized, 0)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.3 }}>
                                      = вывел в блокчейн − завёл (факт). Монеты в балансе/стейке
                                      ещё в системе — это НЕ PNL, игрок ими ещё играет.
                                    </Typography>
                                  </>
                                );
                              })()}
                            </Box>
                          </Box>
                        </Grid>

                        {/* USDT рефералы (если есть) */}
                        {parseFloat(user.earnings_breakdown?.referral_marathon_usdt || 0) > 0 && (
                          <Grid item xs={12}>
                            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(34, 197, 94, 0.1)', borderRadius: 2, border: '1px solid #22c55e' }}>
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                💵 Реферальные от Marathon (USDT)
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                                {formatNumber(user.earnings_breakdown?.referral_marathon_usdt || 0, 2)} USDT
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Блокчейн-обороты: сколько реально завёл/вывел ончейн (SPERM/USDT раздельно) */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1 }}>
                        ⛓️ Блокчейн-обороты
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(139, 92, 246, 0.12)', borderRadius: 2, border: '1px solid #8b5cf6' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>SPERM</Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Завёл (ончейн):</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(blockchainTurnover?.sperm_deposited || 0, 2)} SPERM
                                  <Box component="span" sx={{ color: '#64748b', ml: 0.5 }}>
                                    ≈ {formatNumber(blockchainTurnover?.sperm_deposited_ton || 0, 2)} TON
                                  </Box>
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Вывел (ончейн):</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(blockchainTurnover?.sperm_withdrawn || 0, 2)} SPERM
                                  <Box component="span" sx={{ color: '#64748b', ml: 0.5 }}>
                                    ≈ {formatNumber(blockchainTurnover?.sperm_withdrawn_ton || 0, 2)} TON
                                  </Box>
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(38, 161, 123, 0.12)', borderRadius: 2, border: '1px solid #26A17B' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>USDT</Typography>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Завёл (ончейн):</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(blockchainTurnover?.usdt_deposited || 0, 2)} USDT
                                </Typography>
                              </Box>
                              {/* Расшифровка: марафон платится прямо с кошелька мимо
                                  таблицы deposits, поэтому показываем откуда сумма. */}
                              {(blockchainTurnover?.usdt_deposited_marathon > 0) && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: '#475569', pl: 1 }}>
                                    из них: пополнения / марафон
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    {formatNumber(blockchainTurnover?.usdt_deposited_wallet || 0, 2)} / {formatNumber(blockchainTurnover?.usdt_deposited_marathon || 0, 2)} USDT
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Вывел (ончейн):</Typography>
                                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                  {formatNumber(blockchainTurnover?.usdt_withdrawn || 0, 2)} USDT
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>

                        {/* ИТОГ по живым деньгам: завёл минус вывел. SPERM пересчитан в TON
                            по курсу ДНЯ операции — в токенах сравнивать нельзя, курс ходил в разы. */}
                        <Grid item xs={12}>
                          {(() => {
                            const bt = blockchainTurnover || {};
                            const netTon = bt.net_ton || 0;
                            const netUsdt = bt.net_usdt || 0;
                            const netUsd = bt.net_usd;
                            const plus = (netUsd != null ? netUsd : netTon) >= 0;
                            const sgn = plus ? '+' : '';
                            return (
                              <Box sx={{
                                p: 2, borderRadius: 2,
                                border: `1px solid ${plus ? '#22c55e' : '#ef4444'}`,
                                bgcolor: plus ? 'rgba(34, 197, 94, 0.10)' : 'rgba(239, 68, 68, 0.10)'
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
                                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                    {plus ? 'Мы с него в ПЛЮСЕ:' : 'Мы с него в МИНУСЕ:'}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: plus ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                                    {sgn}{formatNumber(netTon, 2)} TON
                                    {netUsdt !== 0 && ` и ${netUsdt > 0 ? '+' : ''}${formatNumber(netUsdt, 2)} USDT`}
                                    {netUsd != null && ` (${sgn}$${formatNumber(netUsd, 2)})`}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  Завёл {formatNumber(bt.sperm_deposited_ton || 0, 2)} TON · вывел {formatNumber(bt.sperm_withdrawn_ton || 0, 2)} TON.
                                  SPERM пересчитан в TON по курсу дня операции. Админские начисления и то,
                                  что игрок ещё держит в игре (баланс, стейки, MoonStash), сюда НЕ входят.
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* 🍬 SUGAR STRIKE (USDT) — отдельная касса. Валюта та же, что у марафона,
                      и путь вывода один, поэтому владелец (06.09.2026) попросил видеть
                      ИМЕННО игровые USDT отдельной строкой. Показываем, только если играл. */}
                  {user.sugar_strike?.played && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #f472b6' }}>
                        <Typography variant="subtitle2" sx={{ color: '#f472b6', mb: 1 }}>
                          🍬 Sugar Strike (USDT)
                        </Typography>
                        {(() => {
                          const ss = user.sugar_strike || {};
                          // Знак и цвет — со стороны ДОМА, как во всей этой карточке:
                          // игрок в плюсе = красный (мы отдали), игрок в минусе = зелёный.
                          const net = ss.game_net || 0;
                          const total = ss.usdt_total || 0;
                          const colHouse = (n) => (n >= 0 ? '#ef4444' : '#10b981');
                          const sgn = (n) => (n >= 0 ? '+' : '');
                          const row = (label, value, opts = {}) => (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                              <Typography variant={opts.small ? 'caption' : 'body2'} sx={{ color: opts.small ? '#475569' : '#64748b', pl: opts.small ? 1 : 0 }}>
                                {label}
                              </Typography>
                              <Typography variant={opts.small ? 'caption' : 'body2'} sx={{ color: opts.color || (opts.small ? '#94a3b8' : '#e2e8f0'), fontWeight: opts.bold ? 'bold' : 'normal' }}>
                                {value}
                              </Typography>
                            </Box>
                          );
                          return (
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(244, 114, 182, 0.10)', borderRadius: 2, border: '1px solid #be185d' }}>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Игра</Typography>
                                  <Box sx={{ mt: 1 }}>
                                    {row('Поставил:', `${formatNumber(ss.wager || 0, 2)} USDT`)}
                                    {row('Выплачено:', `${formatNumber(ss.payout || 0, 2)} USDT`)}
                                    {ss.pool_paid > 0 && row('из них мини-джекпот:', `${formatNumber(ss.pool_paid, 2)} USDT`, { small: true })}
                                    {ss.modules_spent > 0 && row('Куплено модулей:', `−${formatNumber(ss.modules_spent, 2)} USDT`, { small: true })}
                                    {ss.rtp != null && row('RTP игрока:', `${(ss.rtp * 100).toFixed(2)}%`)}
                                    {ss.owed !== 0 && row('Касса-возврат (owed):', `${formatNumber(ss.owed || 0, 2)} USDT`, { small: true })}
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(244, 114, 182, 0.10)', borderRadius: 2, border: '1px solid #be185d' }}>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Начисления сверху игры</Typography>
                                  <Box sx={{ mt: 1 }}>
                                    {row('Призы недельной лиги:', `${formatNumber(ss.league_prizes || 0, 2)} USDT`)}
                                    {row('Подарок по реф-ссылке SS:', `${formatNumber(ss.ref_gift || 0, 2)} USDT`)}
                                    {row('Награда за друга в SS:', `${formatNumber(ss.ref_reward || 0, 2)} USDT`)}
                                    {row('Выстрелов:', formatNumber(ss.shots || 0, 0), { small: true })}
                                    {row('Боссов добито (бок./верх.):', `${ss.side_bosses || 0} / ${ss.top_bosses || 0}`, { small: true })}
                                    {ss.first_day && row('Играл:', `${String(ss.first_day).slice(0, 10)} — ${String(ss.last_day).slice(0, 10)}`, { small: true })}
                                  </Box>
                                </Box>
                              </Grid>
                              {/* §07.09.2026 (владелец): «профит дома — это не профит дома,
                                  если человек играет на подарочные». Отдельным блоком: чьи
                                  деньги крутятся, сколько подарка сожжено и сколько осталось
                                  открутить по вагеру ×20. */}
                              <Grid item xs={12}>
                                <Box sx={{
                                  p: { xs: 1.5, sm: 2 }, borderRadius: 2,
                                  bgcolor: ss.free_total > 0 ? 'rgba(244, 195, 74, 0.10)' : 'rgba(148, 163, 184, 0.08)',
                                  border: `1px solid ${ss.free_total > 0 ? '#f4c34a' : '#334155'}`
                                }}>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    Чьи деньги в игре
                                    {ss.free_total > 0 && ss.dep_real === 0 && (
                                      <Box component="span" sx={{ ml: 1, px: 0.6, borderRadius: 0.5, fontSize: 11, fontWeight: 700, color: '#f4c34a', border: '1px solid #f4c34a88' }}>
                                        🎁 играет ТОЛЬКО на подарочные
                                      </Box>
                                    )}
                                  </Typography>
                                  <Box sx={{ mt: 1 }}>
                                    {row('🎁 Подарочных выдано:', `${formatNumber(ss.free_total || 0, 2)} USDT`,
                                      { color: '#f4c34a', bold: true })}
                                    {ss.free_total > 0 && row('из них сожжено в игре:', `${formatNumber(ss.gift_burned || 0, 2)} USDT`, { small: true })}
                                    {ss.free_total > 0 && row('осталось подарочных:', `${formatNumber(ss.gift_left || 0, 2)} USDT`, { small: true })}
                                    {ss.gift_target > 0 && row('Отыгрыш подарка ×20:',
                                      ss.gift_released
                                        ? '🔓 отыгран, лок снят'
                                        : `🔒 ${formatNumber(ss.gift_wagered || 0, 2)} из ${formatNumber(ss.gift_target, 2)} USDT (${Math.floor(((ss.gift_wagered || 0) / ss.gift_target) * 100)}%)`,
                                      { color: ss.gift_released ? '#22c55e' : '#e2e8f0' })}
                                    {row('💵 Занёс своих в казино:', `${formatNumber(ss.dep_real || 0, 2)} USDT`,
                                      { color: ss.dep_real > 0 ? '#22c55e' : '#64748b' })}
                                    {row('Профит дома РЕАЛЬНЫЙ:', `${sgn(ss.house_real || 0)}${formatNumber(ss.house_real || 0, 2)} USDT`,
                                      { color: (ss.house_real || 0) > 0 ? '#22c55e' : '#94a3b8', bold: true })}
                                    {ss.house_gift > 0 && row('из «профита» — возврат подарка:', `${formatNumber(ss.house_gift, 2)} USDT`, { small: true, color: '#f4c34a' })}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                    Подарочные — это НАШИ деньги (подарок за рефералку, награда
                                    за друга, призы лиги). Пока человек крутит их, «профит дома»
                                    лишь возвращает в кассу подаренное и заработком не является.
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{
                                  p: 2, borderRadius: 2,
                                  border: `1px solid ${total >= 0 ? '#ef4444' : '#22c55e'}`,
                                  bgcolor: total >= 0 ? 'rgba(239, 68, 68, 0.10)' : 'rgba(34, 197, 94, 0.10)'
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 'bold' }}>
                                      {total >= 0 ? 'Забрал из Sugar Strike:' : 'Оставил в Sugar Strike:'}
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: colHouse(total), fontWeight: 'bold' }}>
                                      {sgn(total)}{formatNumber(total, 2)} USDT
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.3 }}>
                                    По самой игре {sgn(net)}{formatNumber(net, 2)} USDT
                                    {(ss.league_prizes > 0 || ss.ref_gift > 0 || ss.ref_reward > 0) &&
                                      ` + лига/рефералка ${formatNumber((ss.league_prizes || 0) + (ss.ref_gift || 0) + (ss.ref_reward || 0), 2)} USDT`}.
                                    Это ТОЛЬКО Sugar Strike — марафонные USDT сюда не входят,
                                    хотя лежат на том же балансе и выводятся тем же путём.
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          );
                        })()}
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        Общий доход (в users)
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(user.daily_earnings || 0, 2)} SPERM
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        Реферальный бонус (в users)
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(user.referral_bonus || 0, 2)} SPERM
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        APR (детализация)
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>Базовый:</Typography>
                          <Typography variant="body2">{parseFloat(user.lunar_apr || 0).toFixed(2)}%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>+ VIP бонус:</Typography>
                          <Typography variant="body2" sx={{ color: '#6366f1' }}>+{parseFloat(user.vip_bonus_apr || 0).toFixed(3)}%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>+ Ранг бонус:</Typography>
                          <Typography variant="body2" sx={{ color: '#f59e0b' }}>+{parseFloat(user.rank_bonus_apr || 0).toFixed(3)}%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>+ Лидер бонус:</Typography>
                          <Typography variant="body2" sx={{ color: '#10b981' }}>+{parseFloat(user.leader_bonus_apr || 0).toFixed(3)}%</Typography>
                        </Box>
                        <Divider sx={{ my: 1, borderColor: '#334155' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Эффект. APR:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                            {(parseFloat(user.lunar_apr || 0) + parseFloat(user.total_bonus_apr || 0)).toFixed(3)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        Депозиты
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(user.total_deposited || 0, 2)} SPERM
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        Выводы
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(user.total_withdrawn || 0, 2)} SPERM
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                      <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
                        Скорость майнинга
                      </Typography>
                      <Typography variant="h6">
                        {user.mining_rate || 83.3} SPERM/claim
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Ввод / Вывод — живые деньги за ВСЁ время */}
              {activeTab === 1 && (
                <Box>
                  {moneyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label={`Завёл · ${moneyTotals?.count_in || 0} шт.`}
                            value={`${formatNumber(moneyTotals?.in_sperm || 0, 0)} SPERM`}
                            sub={`≈ ${formatNumber(moneyTotals?.in_ton || 0, 2)} TON${moneyTotals?.in_usdt ? ` · ${formatNumber(moneyTotals.in_usdt, 2)} USDT` : ''}`}
                            color="#38bdf8"
                            accent="#38bdf8"
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label={`Вывел · ${moneyTotals?.count_out || 0} шт.`}
                            value={`${formatNumber(moneyTotals?.out_sperm || 0, 0)} SPERM`}
                            sub={`≈ ${formatNumber(moneyTotals?.out_ton || 0, 2)} TON${moneyTotals?.out_usdt ? ` · ${formatNumber(moneyTotals.out_usdt, 2)} USDT` : ''}`}
                            color="#a78bfa"
                            accent="#a78bfa"
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label="Ждёт отправки"
                            value={`${formatNumber(moneyTotals?.pending_sperm || 0, 0)} SPERM`}
                            sub={moneyTotals?.pending_usdt ? `${formatNumber(moneyTotals.pending_usdt, 2)} USDT` : 'заявки в очереди'}
                            color={(moneyTotals?.pending_sperm || moneyTotals?.pending_usdt) ? '#f59e0b' : '#64748b'}
                            accent={(moneyTotals?.pending_sperm || moneyTotals?.pending_usdt) ? '#f59e0b' : '#334155'}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label="Руки админа"
                            value={`+${formatNumber(moneyTotals?.admin_in || 0, 0)} / −${formatNumber(moneyTotals?.admin_out || 0, 0)}`}
                            sub="начислено / списано вручную"
                            color="#f472b6"
                            accent="#334155"
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                        {isMobile
                          ? 'Только реальные деньги за всё время. SPERM пересчитан в TON по курсу дня операции.'
                          : `Всё движение реальных денег за всё время: своп TON→SPERM, прямые переводы SPERM,
                             пополнения USDT, взносы марафона и выводы (включая отклонённые). SPERM пересчитан
                             в TON по курсу дня операции. Внутриигровые начисления сюда не входят — они на
                             вкладке «Транзакции».`}
                      </Typography>

                      <TableContainer component={Paper} sx={{ bgcolor: '#0f172a', border: '1px solid #334155', overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 720 }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#1e293b' }}>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Дата</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Направление</TableCell>
                              <TableCell align="right" sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Сумма</TableCell>
                              <TableCell align="right" sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>≈ TON</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Статус</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Кошелёк / хэш</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {moneyFlow.length > 0 ? moneyFlow.slice(0, moneyShown).map((row) => {
                              const isIn = row.direction === 'in';
                              return (
                                <TableRow key={`${row.source}-${row.direction}-${row.id}`} hover>
                                  <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>
                                    {formatDateTime(row.date)}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {isIn
                                        ? <ArrowDownward sx={{ fontSize: 16, color: '#22c55e' }} />
                                        : <ArrowUpward sx={{ fontSize: 16, color: '#ef4444' }} />}
                                      <Typography variant="body2" sx={{ color: isIn ? '#22c55e' : '#ef4444' }}>
                                        {isIn ? 'Ввод' : 'Вывод'}
                                      </Typography>
                                      {row.kind === 'admin' && (
                                        <Chip label="админ" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#334155', color: '#cbd5e1' }} />
                                      )}
                                    </Box>
                                    {row.description && (
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        {row.description}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right" sx={{
                                    color: isIn ? '#22c55e' : '#ef4444',
                                    borderBottom: '1px solid #334155',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {isIn ? '+' : '−'}{formatNumber(row.amount, 2)} {row.currency}
                                    {row.fee > 0 && (
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        комиссия {formatNumber(row.fee, 2)}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>
                                    {row.ton_amount != null ? formatNumber(row.ton_amount, 2) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                    <Chip
                                      label={getMoneyStatusLabel(row.status)}
                                      size="small"
                                      sx={{
                                        bgcolor: 'transparent',
                                        border: `1px solid ${MONEY_STATUS_COLOR[row.status] || '#334155'}`,
                                        color: MONEY_STATUS_COLOR[row.status] || '#94a3b8'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                    {row.wallet && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                        <Typography variant="caption" sx={{ color: '#38bdf8', fontFamily: 'monospace' }}>
                                          {shortHash(row.wallet)}
                                        </Typography>
                                        <Tooltip title="Скопировать адрес">
                                          <IconButton size="small" onClick={() => copyToClipboard(row.wallet)} sx={{ color: '#475569', p: 0.2 }}>
                                            <ContentCopy sx={{ fontSize: 12 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                    {row.tx_hash ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                                          {shortHash(row.tx_hash)}
                                        </Typography>
                                        <Tooltip title="Скопировать хэш">
                                          <IconButton size="small" onClick={() => copyToClipboard(row.tx_hash)} sx={{ color: '#475569', p: 0.2 }}>
                                            <ContentCopy sx={{ fontSize: 12 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ) : (
                                      row.kind === 'onchain' && (
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                          хэша нет
                                        </Typography>
                                      )
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            }) : (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', py: 3 }}>
                                  Ни одного ввода или вывода — игрок не заводил и не выводил деньги
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {moneyFlow.length > moneyShown && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setMoneyShown(moneyShown + 100)}
                            sx={{ color: '#e2e8f0', borderColor: '#334155' }}
                          >
                            Показать ещё ({moneyFlow.length - moneyShown})
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Транзакции */}
              {activeTab === 2 && (
                <Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {TX_PERIODS.map((period) => (
                      <Chip
                        key={period.key}
                        label={period.label}
                        size="small"
                        onClick={() => setTxPeriod(period.key)}
                        sx={{
                          bgcolor: txPeriod === period.key ? '#6366f1' : 'transparent',
                          color: txPeriod === period.key ? 'white' : '#94a3b8',
                          border: '1px solid #334155'
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Chip
                      label="Все типы"
                      size="small"
                      onClick={() => setTxGroup('all')}
                      sx={{
                        bgcolor: txGroup === 'all' ? '#0ea5e9' : 'transparent',
                        color: txGroup === 'all' ? 'white' : '#94a3b8',
                        border: '1px solid #334155'
                      }}
                    />
                    {Object.entries(TX_GROUPS).map(([key, group]) => (
                      <Chip
                        key={key}
                        label={group.label}
                        size="small"
                        onClick={() => setTxGroup(key)}
                        sx={{
                          bgcolor: txGroup === key ? '#0ea5e9' : 'transparent',
                          color: txGroup === key ? 'white' : '#94a3b8',
                          border: '1px solid #334155'
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Найдено: {transactionsTotal}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={transactionsPage <= 1}
                        onClick={() => loadTransactions(transactionsPage - 1)}
                        sx={{ color: '#e2e8f0', borderColor: '#334155' }}
                      >
                        ←
                      </Button>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        {transactionsPage} / {Math.ceil(transactionsTotal / 50) || 1}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={transactionsPage >= Math.ceil(transactionsTotal / 50)}
                        onClick={() => loadTransactions(transactionsPage + 1)}
                        sx={{ color: '#e2e8f0', borderColor: '#334155' }}
                      >
                        →
                      </Button>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} sx={{ bgcolor: '#0f172a', border: '1px solid #334155', maxHeight: 440, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#1e293b' }}>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', bgcolor: '#1e293b' }}>Дата</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', bgcolor: '#1e293b' }}>Тип</TableCell>
                          <TableCell align="right" sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', bgcolor: '#1e293b' }}>Сумма</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', bgcolor: '#1e293b' }}>Статус</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155', bgcolor: '#1e293b' }}>Описание</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.length > 0 ? (
                          transactions.map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                {formatDateTime(tx.created_at)}
                              </TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                <Chip
                                  label={getTransactionTypeLabel(tx.type)}
                                  size="small"
                                  color={tx.type.includes('deposit') || tx.type.includes('bonus') || tx.type === 'claim' ? 'success' : tx.type.includes('withdrawal') ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{
                                color: parseFloat(tx.amount) < 0 ? '#ef4444' : '#22c55e',
                                borderBottom: '1px solid #334155',
                                whiteSpace: 'nowrap',
                                fontWeight: 600
                              }}>
                                {parseFloat(tx.amount) > 0 ? '+' : ''}{formatNumber(tx.amount, 2)}
                                {tx.currency && tx.currency !== 'SPERM' && (
                                  <Box component="span" sx={{ color: '#64748b', ml: 0.5 }}>{tx.currency}</Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                <Chip
                                  label={getTransactionStatusLabel(tx.status)}
                                  size="small"
                                  color={tx.status === 'completed' ? 'success' : 'warning'}
                                />
                              </TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>{tx.description}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                              Нет транзакций по выбранному фильтру
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Стейкинги */}
              {activeTab === 3 && (
                <Box>
                  {/* Суммарная информация по стейкингам */}
                  <Paper sx={{ p: 2, mb: 2, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                            {formatNumber(stakings.filter(s => s.status === 'active').reduce((sum, s) => sum + parseFloat(s.visual_balance || 0), 0), 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Всего в стейкинге (SPERM)
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ color: '#6366f1' }}>
                            {formatNumber(stakings.filter(s => s.type === 'lunar' && s.status === 'active').reduce((sum, s) => sum + parseFloat(s.visual_balance || 0), 0), 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            🌙 Lunar стейкинг
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ color: '#eab308' }}>
                            {formatNumber(stakings.filter(s => s.type === 'star' && s.status === 'active').reduce((sum, s) => sum + parseFloat(s.visual_balance || 0), 0), 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            ⭐ Star стейкинг
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ color: '#f97316' }}>
                            {formatNumber(stakings.filter(s => s.type === 'marathon').reduce((sum, s) => sum + parseFloat(s.actual_amount || s.amount || 0), 0), 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            🏃 Лунный марафон
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  <TableContainer component={Paper} sx={{ bgcolor: '#0f172a', border: '1px solid #334155', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 860 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#1e293b' }}>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Тип</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Депозит</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Визуальный</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Base APR</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Эффект. APR</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Pending</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Дней</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Статус</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stakings.length > 0 ? stakings.map((staking) => (
                          <TableRow key={staking.id}>
                            <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                              <Chip
                                label={staking.type === 'lunar' ? '🌙 Lunar' : staking.type === 'star' ? '⭐ Star' : staking.type === 'marathon' ? '🏃 Марафон' : staking.type}
                                size="small"
                                sx={{
                                  bgcolor: staking.type === 'lunar' ? '#6366f1' : staking.type === 'star' ? '#eab308' : staking.type === 'marathon' ? '#f97316' : '#64748b',
                                  color: 'white'
                                }}
                              />
                            </TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                            {formatNumber(staking.actual_amount || staking.amount, 0)}
                          </TableCell>
                          <TableCell sx={{ color: '#8b5cf6', borderBottom: '1px solid #334155', fontWeight: 'bold' }}>
                            {staking.type === 'lunar' ? formatNumber(staking.visual_balance || 0, 2) : '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                            {parseFloat(staking.apr || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell sx={{ color: '#10b981', borderBottom: '1px solid #334155', fontWeight: 'bold' }}>
                            {parseFloat(staking.effective_apr || staking.apr || 0).toFixed(3)}%
                          </TableCell>
                          <TableCell sx={{ color: '#f59e0b', borderBottom: '1px solid #334155' }}>
                            {formatNumber(staking.pending_profit || 0, 2)}
                          </TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                            {staking.days_active || 0}
                          </TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                            <Chip
                              label={staking.status}
                              size="small"
                              sx={{
                                bgcolor: staking.status === 'active' ? '#10b981' : '#ef4444',
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                handleDeleteStaking(staking.id);
                              }}
                              sx={{
                                color: '#ef4444',
                                '&:hover': {
                                  backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                }
                              }}
                              title="Удалить стейкинг"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                            Нет активных стейкингов
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              )}

              {/* Рефералы */}
              {activeTab === 4 && (
                <Box>
                  {/* Статистика рефералов */}
                  <Paper sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Реферальная статистика
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#6366f1' }}>
                            {user.referral_count || 0}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Всего рефералов
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#10b981' }}>
                            {formatNumber(user.referral_earnings || 0, 2)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Заработано SPERM
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Статистика по линиям */}
                    {referralStats && referralStats.levelStats && (
                      <>
                        <Divider sx={{ my: 2, borderColor: '#334155' }} />

                        {/* Суммарный стейкинг команды */}
                        <Box sx={{
                          p: 2,
                          mb: 2,
                          bgcolor: 'rgba(16, 185, 129, 0.15)',
                          borderRadius: 2,
                          border: '2px solid #10b981'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1 }}>
                            💎 Суммарно в стейкинге у всей команды:
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                            {formatNumber(
                              [1, 2, 3, 4, 5].reduce((sum, level) => {
                                const stats = referralStats.levelStats[`level${level}`] || {};
                                return sum + (parseFloat(stats.totalStaking) || 0);
                              }, 0),
                              0
                            )} SPERM
                          </Typography>
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 2, color: '#94a3b8' }}>
                          Рефералы по линиям
                        </Typography>
                        <Grid container spacing={1}>
                          {[1, 2, 3, 4, 5].map(level => {
                            const stats = referralStats.levelStats[`level${level}`] || {};
                            return (
                              <Grid item xs={12} key={level}>
                                <Box sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: { xs: 1, sm: 1.5 },
                                  bgcolor: stats.referralCount > 0 ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                                  borderRadius: 1,
                                  border: stats.referralCount > 0 ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid #334155'
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label={`${level}-я линия`}
                                      size="small"
                                      sx={{
                                        bgcolor: level === 1 ? '#10b981' : level === 2 ? '#6366f1' : level === 3 ? '#8b5cf6' : level === 4 ? '#a855f7' : '#c084fc',
                                        color: 'white',
                                        fontWeight: 600
                                      }}
                                    />
                                    <Typography sx={{ color: '#e2e8f0' }}>
                                      {stats.referralCount || 0} чел.
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
                                    <Box sx={{ textAlign: 'right', minWidth: 58 }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Стейкинг</Typography>
                                      <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                                        {formatNumber(stats.totalStaking || 0, 0)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', minWidth: 58 }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Депозиты</Typography>
                                      <Typography variant="body2" sx={{ color: '#6366f1' }}>
                                        {formatNumber(stats.totalDeposits || 0, 0)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', minWidth: 58 }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Бонус</Typography>
                                      <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                                        {formatNumber(stats.totalBonus || 0, 2)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', minWidth: 58 }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Активных</Typography>
                                      <Typography variant="body2" sx={{ color: '#8b5cf6' }}>
                                        {stats.activeCount || 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </>
                    )}

                    <Divider sx={{ my: 2, borderColor: '#334155' }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#94a3b8' }}>
                      Реферальный код
                    </Typography>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'rgba(123, 63, 242, 0.1)',
                      border: '1px solid rgba(123, 63, 242, 0.3)',
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#7b3ff2' }}>
                        {user.referral_code || 'Не назначен'}
                      </Typography>
                    </Box>

                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#64748b' }}>
                      Реферальная ссылка: https://t.me/moonraid_game_bot?start={user.referral_code}
                    </Typography>
                  </Paper>

                  {/* Список рефералов */}
                  <Paper sx={{ p: { xs: 1.5, sm: 3 }, mt: 3, bgcolor: '#0f172a', border: '1px solid #334155' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group sx={{ color: '#6366f1' }} />
                      Рефералы ({referrals.length})
                    </Typography>

                    {referrals.length > 0 ? (
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 560 }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#1e293b' }}>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Пользователь</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Баланс</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>В стейкинге</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Дата рег.</TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Рефералы</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {referrals.map((ref) => (
                              <TableRow key={ref.id}>
                                <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#6366f1', fontSize: 12 }}>
                                      {(ref.username || 'U')[0].toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2">{ref.username || `ID: ${ref.telegram_id}`}</Typography>
                                      <Typography variant="caption" sx={{ color: '#64748b' }}>{ref.telegram_id}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: '#6366f1', borderBottom: '1px solid #334155' }}>
                                  {formatNumber(ref.balance || 0, 0)}
                                </TableCell>
                                <TableCell sx={{ color: '#10b981', borderBottom: '1px solid #334155', fontWeight: 'bold' }}>
                                  {formatNumber(ref.activeStaking || ref.active_staking || 0, 0)}
                                </TableCell>
                                <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                  {formatDate(ref.created_at)}
                                </TableCell>
                                <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                  <Chip
                                    label={ref.referral_count || 0}
                                    size="small"
                                    sx={{
                                      bgcolor: ref.referral_count > 0 ? '#6366f1' : '#475569',
                                      color: 'white'
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info" sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)' }}>
                        У пользователя нет рефералов
                      </Alert>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Предупреждения */}
              {activeTab === 5 && (
                <Box>
                  {warnings.length > 0 ? (
                    <TableContainer component={Paper} sx={{ bgcolor: '#0f172a', border: '1px solid #334155', overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 520 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#1e293b' }}>
                            <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Дата</TableCell>
                            <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Причина</TableCell>
                            <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Комментарий</TableCell>
                            <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>Выдал</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {warnings.map((warning) => (
                            <TableRow key={warning.id}>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                {formatDateTime(warning.created_at)}
                              </TableCell>
                              <TableCell sx={{ color: '#fbbf24', borderBottom: '1px solid #334155' }}>
                                {warning.reason}
                              </TableCell>
                              <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                {warning.comment || '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                {warning.admin_username || 'Система'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)' }}>
                      У пользователя нет предупреждений
                    </Alert>
                  )}

                  {/* Информация о блокировке */}
                  {user.is_blocked && (
                    <Paper sx={{ mt: 2, p: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444' }}>
                      <Typography variant="subtitle1" sx={{ color: '#ef4444', fontWeight: 'bold', mb: 1 }}>
                        Пользователь заблокирован
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                        Причина: {user.block_reason || 'Не указана'}
                      </Typography>
                      {user.blocked_until && (
                        <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                          До: {formatDateTime(user.blocked_until)}
                        </Typography>
                      )}
                      {user.blocked_at && (
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Заблокирован: {formatDateTime(user.blocked_at)}
                        </Typography>
                      )}
                    </Paper>
                  )}

                  {/* Информация о блокировке выводов */}
                  <Paper sx={{
                    mt: 2,
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: user.withdrawal_blacklisted ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    border: user.withdrawal_blacklisted ? '1px solid #ef4444' : '1px solid #10b981'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2
                    }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{
                          color: user.withdrawal_blacklisted ? '#ef4444' : '#10b981',
                          fontWeight: 'bold',
                          mb: 0.5,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}>
                          {user.withdrawal_blacklisted ? '🚫 Выводы заблокированы' : '✅ Выводы разрешены'}
                        </Typography>
                        {user.withdrawal_blacklisted && (
                          <>
                            <Typography variant="body2" sx={{ color: '#e2e8f0', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              Причина: {user.withdrawal_blacklist_reason || 'Не указана'}
                            </Typography>
                            {user.withdrawal_blacklisted_at && (
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                Заблокировано: {formatDateTime(user.withdrawal_blacklisted_at)}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        color={user.withdrawal_blacklisted ? 'success' : 'error'}
                        size="small"
                        onClick={async () => {
                          try {
                            if (user.withdrawal_blacklisted) {
                              const response = await adminAPI.removeUserFromWithdrawalBlacklist(userId);
                              if (response.data.success) {
                                alert('Выводы разблокированы');
                                loadUserDetails();
                              } else {
                                alert('Ошибка: ' + (response.data.error || 'Неизвестная ошибка'));
                              }
                            } else {
                              const reason = window.prompt('Укажите причину блокировки выводов:');
                              if (reason) {
                                const response = await adminAPI.addUserToWithdrawalBlacklist(userId, reason);
                                if (response.data.success) {
                                  alert('Выводы заблокированы');
                                  loadUserDetails();
                                } else {
                                  alert('Ошибка: ' + (response.data.error || 'Неизвестная ошибка'));
                                }
                              }
                            }
                          } catch (error) {
                            console.error('Ошибка:', error);
                            alert('Ошибка: ' + (error.response?.data?.error || error.message));
                          }
                        }}
                        sx={{
                          minWidth: { xs: '100%', sm: 150 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {user.withdrawal_blacklisted ? 'Разблокировать' : 'Заблокировать'}
                      </Button>
                    </Box>
                  </Paper>

                  {/* Блок управления реферальной программой */}
                  <Paper sx={{
                    mt: 2,
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: user.referral_suspended ? 'rgba(251, 146, 60, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                    border: user.referral_suspended ? '1px solid #fb923c' : '1px solid #6366f1'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2
                    }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{
                          color: user.referral_suspended ? '#fb923c' : '#6366f1',
                          fontWeight: 'bold',
                          mb: 0.5,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}>
                          {user.referral_suspended ? '⏸️ Реферальная программа приостановлена' : '👥 Реферальная программа активна'}
                        </Typography>
                        {user.referral_suspended && (
                          <>
                            <Typography variant="body2" sx={{ color: '#e2e8f0', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              Пользователь не получает реферальные награды и уведомления
                            </Typography>
                            {user.referral_suspended_at && (
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                Приостановлено: {formatDateTime(user.referral_suspended_at)}
                              </Typography>
                            )}
                          </>
                        )}
                        {!user.referral_suspended && (
                          <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Пользователь получает все реферальные награды и уведомления
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        color={user.referral_suspended ? 'primary' : 'warning'}
                        size="small"
                        onClick={async () => {
                          try {
                            const action = user.referral_suspended ? 'возобновить' : 'приостановить';
                            const reason = !user.referral_suspended
                              ? window.prompt(`Укажите причину приостановки реферальной программы (необязательно):`)
                              : null;

                            if (!user.referral_suspended && reason === null) {
                              return; // Отменено пользователем
                            }

                            const response = await adminAPI.toggleReferralSuspension(userId, reason);
                            if (response.data.success) {
                              alert(response.data.message);
                              loadUserDetails();
                            } else {
                              alert('Ошибка: ' + (response.data.error || 'Неизвестная ошибка'));
                            }
                          } catch (error) {
                            console.error('Ошибка:', error);
                            alert('Ошибка: ' + (error.response?.data?.error || error.message));
                          }
                        }}
                        sx={{
                          minWidth: { xs: '100%', sm: 150 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {user.referral_suspended ? 'Возобновить' : 'Приостановить'}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* Настройки */}
              {activeTab === 6 && (
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Язык"
                      secondary={user.language_code || 'ru'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Графика"
                      secondary={user.graphics_quality || 'high'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Звук"
                      secondary={user.sound_enabled ? 'Включен' : 'Выключен'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Уведомления"
                      secondary={user.notifications_enabled ? 'Включены' : 'Выключены'}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="IP адрес"
                      secondary={user.ip_address || 'Не определен'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Страна"
                      secondary={getCountryNameRu(user.country_code)}
                    />
                  </ListItem>
                </List>
              )}
            </Box>
          </>
        ) : (
          <Typography>Пользователь не найден</Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ bgcolor: '#1e293b', borderTop: '1px solid #334155', flexWrap: 'wrap', gap: 1 }}>
        {user?.username && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.open(`/wallet-scanner?profit=${user.username}`, '_blank')}
            sx={{ color: '#7c3aed', borderColor: '#7c3aed', fontSize: { xs: '0.7rem', md: '0.8rem' } }}
          >
            Профит-сканер
          </Button>
        )}
        <Button onClick={onClose} sx={{ color: '#94a3b8' }}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsModal;